// ==================== SELECT COMPONENT ====================

import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  forwardRef,
  useId,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import type { ForwardedRef, ReactNode } from 'react';
import { ChevronDown, X, Search, Check, AlertCircle } from 'lucide-react';
import { cn } from '../../utils/helpers';

function assignForwardedRef<T>(ref: ForwardedRef<T>, node: T | null) {
  if (typeof ref === 'function') ref(node);
  else if (ref && typeof ref === 'object' && 'current' in ref) {
    (ref as { current: T | null }).current = node;
  }
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: ReactNode;
}

interface SelectProps {
  options: SelectOption[];
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  hint?: string;
  disabled?: boolean;
  required?: boolean;
  searchable?: boolean;
  multiple?: boolean;
  clearable?: boolean;
  fullWidth?: boolean;
  maxHeight?: number;
  name?: string;
  id?: string;
  size?: 'sm' | 'md' | 'lg';
}

const Select = forwardRef<HTMLDivElement | null, SelectProps>(
  (
    {
      options,
      value,
      onChange,
      label,
      placeholder = 'Select an option',
      error,
      hint,
      disabled = false,
      required = false,
      searchable = false,
      multiple = false,
      clearable = false,
      fullWidth = true,
      maxHeight = 250,
      name,
      id,
      size = 'md',
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [menuRect, setMenuRect] = useState<{
      top: number;
      left: number;
      width: number;
    } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement | null>(null);
    const menuPortalRef = useRef<HTMLDivElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const generatedId = useId();
    const selectId = id || generatedId;

    const setTriggerNode = useCallback(
      (node: HTMLDivElement | null) => {
        triggerRef.current = node;
        assignForwardedRef(ref, node);
      },
      [ref],
    );

    const updateMenuPosition = useCallback(() => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMenuRect({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
      });
    }, []);

    useLayoutEffect(() => {
      if (!isOpen) {
        setMenuRect(null);
        return;
      }
      updateMenuPosition();
      const onReposition = () => updateMenuPosition();
      window.addEventListener('scroll', onReposition, true);
      window.addEventListener('resize', onReposition);
      return () => {
        window.removeEventListener('scroll', onReposition, true);
        window.removeEventListener('resize', onReposition);
      };
    }, [isOpen, updateMenuPosition]);

    // Handle click outside to close (trigger + portaled menu)
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (containerRef.current?.contains(target)) return;
        if (menuPortalRef.current?.contains(target)) return;
        setIsOpen(false);
        setSearchQuery('');
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when opened (after portaled menu mounts)
    useLayoutEffect(() => {
      if (isOpen && searchable && menuRect && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isOpen, searchable, menuRect]);

    // Filter options based on search
    const filteredOptions = options.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Get selected values as array
    const selectedValues = multiple
      ? (Array.isArray(value) ? value : [])
      : (value ? [value as string] : []);

    // Get display text
    const getDisplayText = () => {
      if (selectedValues.length === 0) return placeholder;
      
      if (multiple) {
        if (selectedValues.length === 1) {
          const option = options.find((o) => o.value === selectedValues[0]);
          return option?.label || placeholder;
        }
        return `${selectedValues.length} selected`;
      }
      
      const option = options.find((o) => o.value === selectedValues[0]);
      return option?.label || placeholder;
    };

    // Handle option select
    const handleSelect = (optionValue: string) => {
      if (multiple) {
        const newValues = selectedValues.includes(optionValue)
          ? selectedValues.filter((v) => v !== optionValue)
          : [...selectedValues, optionValue];
        onChange?.(newValues);
      } else {
        onChange?.(optionValue);
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    // Handle clear
    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.(multiple ? [] : '');
    };

    // Remove single value (for multiple)
    const handleRemoveValue = (e: React.MouseEvent, valueToRemove: string) => {
      e.stopPropagation();
      if (multiple) {
        onChange?.(selectedValues.filter((v) => v !== valueToRemove));
      }
    };

    // Size-based padding
    const sizeStyles = {
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3',
      lg: 'px-4 py-4 text-lg',
    };

    const baseStyles = `
      relative w-full rounded-md
      bg-(--color-charcoal-black) 
      border border-(--color-border-gray)
      text-white cursor-pointer
      transition-all duration-200
      flex items-center justify-between gap-2
      ${sizeStyles[size]}
    `;

    const focusStyles = isOpen
      ? 'ring-2 ring-(--color-signal-orange)/50 border-(--color-signal-orange)'
      : 'hover:border-(--color-border-gray)/80';

    const errorStyles = error
      ? 'border-(--color-error) focus:ring-(--color-error)/50'
      : '';

    const disabledStyles = disabled
      ? 'opacity-50 cursor-not-allowed'
      : '';

    return (
      <div ref={containerRef} className={cn('relative', fullWidth && 'w-full')}>
        {/* Label */}
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-(--color-soft-gray) mb-2"
          >
            {label}
            {required && <span className="text-(--color-error) ml-1">*</span>}
          </label>
        )}

        {/* Hidden input for form submission */}
        <input
          type="hidden"
          name={name}
          value={multiple ? selectedValues.join(',') : (value as string) || ''}
        />

        {/* Select Trigger */}
        <div
          ref={setTriggerNode}
          id={selectId}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={cn(baseStyles, focusStyles, errorStyles, disabledStyles)}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (!disabled) {
                setIsOpen(!isOpen);
              }
            }
            if (e.key === 'Escape') {
              setIsOpen(false);
            }
          }}
        >
          {/* Selected Values Display */}
          <div className={cn(
            "flex-1 flex items-center gap-2 min-w-0",
            multiple ? "flex-wrap" : "overflow-hidden"
          )}>
            {multiple && selectedValues.length > 0 ? (
              // Multiple selected chips
              selectedValues.map((val) => {
                const option = options.find((o) => o.value === val);
                return (
                  <span
                    key={val}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-(--color-signal-orange)/20 text-(--color-signal-orange) text-sm whitespace-nowrap"
                  >
                    {option?.label}
                    <button
                      type="button"
                      onClick={(e) => handleRemoveValue(e, val)}
                      className="hover:bg-(--color-signal-orange)/30 rounded-full p-0.5"
                    >
                      <X size={12} />
                    </button>
                  </span>
                );
              })
            ) : (
              <span
                className={cn(
                  "truncate whitespace-nowrap",
                  selectedValues.length === 0 && 'text-(--color-muted-gray)'
                )}
              >
                {getDisplayText()}
              </span>
            )}
          </div>

          {/* Right Icons */}
          <div className="flex items-center gap-1">
            {/* Clear Button */}
            {clearable && selectedValues.length > 0 && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-(--color-border-gray)/30 rounded transition-colors"
              >
                <X size={16} className="text-(--color-muted-gray)" />
              </button>
            )}

            {/* Dropdown Arrow */}
            <ChevronDown
              size={18}
              className={cn(
                'text-(--color-muted-gray) transition-transform duration-200',
                isOpen && 'rotate-180'
              )}
            />
          </div>
        </div>

        {/* Dropdown (portal avoids clipping inside overflow/stacking contexts) */}
        {isOpen &&
          menuRect &&
          typeof document !== 'undefined' &&
          createPortal(
            <div
              ref={menuPortalRef}
              className="fixed z-[10000] rounded-md bg-(--color-charcoal-black) border border-(--color-border-gray) shadow-2xl overflow-hidden min-w-[8rem]"
              style={{
                top: menuRect.top,
                left: menuRect.left,
                width: menuRect.width,
                maxHeight,
              }}
              role="listbox"
            >
              {searchable && (
                <div className="p-2 border-b border-(--color-border-gray)">
                  <div className="relative">
                    <Search
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-(--color-muted-gray)"
                    />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search..."
                      className="w-full pl-9 pr-4 py-2 rounded-lg bg-(--color-dark-navy) border border-(--color-border-gray) text-white placeholder:text-(--color-muted-gray) focus:outline-none focus:border-(--color-signal-orange) text-sm"
                    />
                  </div>
                </div>
              )}

              <div
                className="overflow-y-auto overscroll-contain"
                style={{ maxHeight: maxHeight - (searchable ? 60 : 0) }}
              >
                {filteredOptions.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-(--color-muted-gray) text-center">
                    No options found
                  </div>
                ) : (
                  filteredOptions.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <div
                        key={option.value}
                        onClick={() => !option.disabled && handleSelect(option.value)}
                        className={cn(
                          'px-4 py-3 flex items-center gap-3 cursor-pointer transition-colors',
                          isSelected
                            ? 'bg-(--color-signal-orange)/10 text-(--color-signal-orange)'
                            : 'text-(--color-soft-gray) hover:bg-(--color-dark-navy)',
                          option.disabled && 'opacity-50 cursor-not-allowed',
                        )}
                        role="option"
                        aria-selected={isSelected}
                      >
                        {option.icon && (
                          <span className="shrink-0">{option.icon}</span>
                        )}

                        <span className="flex-1 text-sm">{option.label}</span>

                        {isSelected && (
                          <Check size={16} className="shrink-0 text-(--color-signal-orange)" />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>,
            document.body,
          )}

        {/* Error Message */}
        {error && (
          <p className="mt-2 text-sm text-(--color-error) flex items-center gap-1">
            <AlertCircle size={14} />
            {error}
          </p>
        )}

        {/* Hint */}
        {hint && !error && (
          <p className="mt-2 text-sm text-(--color-muted-gray)">{hint}</p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export { Select };

